import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Menu,
  MenuItem,
  Alert,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { CheckCircle, Error, Refresh, Edit } from "@mui/icons-material";
import { MdSpellcheck } from "react-icons/md";

const TECHNICAL_TERMS = new Set([
  "api","rest","json","xml","html","css","js","ts","jsx","tsx",
  "react","angular","vue","node","express","next","redux",
  "mongodb","mysql","aws","docker","kubernetes",
  "typescript","javascript","python","java","mui","materialui"
]);

const SpellCheckComponent = ({ 
  resumeContent, 
  appliedCorrections = [], 
  setAppliedCorrections 
}) => {

  // optional resumeId prop (if the app provides one)
  const resumeId = resumeContent?.resumeId || null;

  const [misspelledWords, setMisspelledWords] = useState([]);
  const [selectedWord, setSelectedWord] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);
  const [correctionText, setCorrectionText] = useState("");

  // ----------------------------
  // SPELL LOGIC
  // ----------------------------

  const isProbablyMisspelled = (word) => {
    if (!word || word.length <= 2) return false;

    const w = word.toLowerCase();

    if (TECHNICAL_TERMS.has(w)) return false;
    if (/\d/.test(word)) return false;
    if (word === word.toUpperCase()) return false;
    if (word[0] === word[0].toUpperCase()) return false;

    if (/(.)\1\1/.test(w)) return true;
    if (/[^aeiou]{6,}/i.test(w)) return true;

    const cons = (w.match(/[^aeiouy]/gi) || []).length;
    const vows = (w.match(/[aeiouy]/gi) || []).length;
    if (cons > vows * 3.5 && w.length > 6) return true;

    return false;
  };

  const generateSuggestions = (word) => {
    const w = word.toLowerCase();
    const suggestions = [];

    if (/(.)\1{2,}/.test(w)) {
      suggestions.push(w.replace(/(.)\1{2,}/g, "$1"));
    }

    if (w.endsWith("er")) suggestions.push(w.replace(/er$/, "or"));
    if (w.endsWith("or")) suggestions.push(w.replace(/or$/, "er"));

    return [...new Set(suggestions)].slice(0, 4);
  };

  // ----------------------------
  // SCAN FUNCTION
  // ----------------------------

  const scanForErrors = () => {
    setScanning(true);

    const el = document.getElementById("resume-preview");
    if (!el) {
      setScanning(false);
      return;
    }

    const text = el.innerText;
    const words = text.split(/\s+/);

    const errors = [];
    const correctedWords = new Set((appliedCorrections || []).map(c => c.word.toLowerCase()));

    words.forEach((word) => {
      const clean = word.replace(/[^\w]/g, "");
      if (!clean) return;
      if (correctedWords.has(clean.toLowerCase())) return;
      if (isProbablyMisspelled(clean)) {
        errors.push({
          word: clean,
          suggestions: generateSuggestions(clean),
        });
      }
    });

    const unique = Array.from(
      new Map(errors.map(item => [item.word, item])).values()
    );

    setMisspelledWords(unique);
    setScanning(false);
    setScanned(true);
  };

  // ----------------------------
  // DIRECT REPLACE IN RESUME
  // ----------------------------

  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const replaceWordInResume = (oldWord, newWord) => {
    const el = document.getElementById("resume-preview");
    if (!el) return;

    // Direct replacement in text nodes for immediate visual feedback
    const walker = document.createTreeWalker(
      el,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          const parent = node.parentElement;
          if (parent && (parent.tagName === 'SCRIPT' || parent.tagName === 'STYLE')) {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        }
      }
    );

    const regex = new RegExp(`\\b${escapeRegExp(oldWord)}\\b`, "g");
    const nodesToUpdate = [];
    let node;
    while ((node = walker.nextNode())) {
      if (regex.test(node.textContent)) {
        nodesToUpdate.push(node);
      }
    }
    nodesToUpdate.forEach((textNode) => {
      textNode.textContent = textNode.textContent.replace(regex, newWord);
    });

    const updated = misspelledWords.filter(w => w.word !== oldWord);
    setMisspelledWords(updated);

    // Update parent's appliedCorrections state
    if (setAppliedCorrections) {
      setAppliedCorrections((prev) => {
        const filtered = prev.filter((c) => c.word !== oldWord);
        return [...filtered, { word: oldWord, suggestion: newWord }];
      });
    }
  };

  // Persist a correction to the backend
  const saveCorrectionToServer = async (oldWord, newWord) => {
    try {
      const token = localStorage.getItem("token");
      await fetch("https://resume-maker-lc.onrender.com/api/spellcorrections", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          resumeId,
          corrections: [{ word: oldWord, suggestion: newWord }],
        }),
      });
    } catch (err) {
      console.error("Failed to save correction", err);
    }
  };

  // ----------------------------
  // HANDLERS
  // ----------------------------

  const handleWordClick = (e, wordInfo) => {
    setSelectedWord(wordInfo);
    setSuggestions(wordInfo.suggestions || []);
    setContextMenu({
      mouseX: e.clientX - 2,
      mouseY: e.clientY - 4,
    });
  };

  const handleReplace = (newWord) => {
    if (!selectedWord) return;
    replaceWordInResume(selectedWord.word, newWord);
    saveCorrectionToServer(selectedWord.word, newWord);
    setContextMenu(null);
  };

  const applyManualCorrection = () => {
    if (!selectedWord || !correctionText.trim()) return;
    const newWord = correctionText.trim();
    replaceWordInResume(selectedWord.word, newWord);
    saveCorrectionToServer(selectedWord.word, newWord);
    setCorrectionDialogOpen(false);
  };

  // Corrections are now fetched and dynamically managed/applied by the parent component.

  // ----------------------------
  // UI
  // ----------------------------

  const renderStatus = () => {

    if (scanning) {
      return (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Scanning resume...</Typography>
        </Paper>
      );
    }

    if (!scanned) {
      return (
        <Paper sx={{ p: 4, textAlign: "center", bgcolor: "#f0f8ff" }}>
          <MdSpellcheck size={48} style={{ color: "#1976d2", marginBottom: 16 }} />
          <Typography variant="h6">Click Check Spelling</Typography>
        </Paper>
      );
    }

    if (misspelledWords.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: "center", bgcolor: "#e8f5e9" }}>
          <CheckCircle sx={{ fontSize: 48, color: "green" }} />
          <Typography>No spelling issues found</Typography>
        </Paper>
      );
    }

    return (
      <>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Found {misspelledWords.length} error(s)
        </Alert>

        {misspelledWords.map((info, idx) => (
          <Paper
            key={idx}
            sx={{ p: 2, mb: 1, bgcolor: "#fff5f5", cursor: "pointer" }}
            onClick={(e) => handleWordClick(e, info)}
          >
            <Typography sx={{ textDecoration: "underline wavy red" }}>
              {info.word}
            </Typography>

            <Box sx={{ mt: 1 }}>
              {info.suggestions.map((sug) => (
                <Chip
                  key={sug}
                  label={sug}
                  size="small"
                  sx={{ mr: 1 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReplace(sug);
                  }}
                />
              ))}

              <Chip
                label="Manual Fix"
                size="small"
                color="secondary"
                icon={<Edit />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedWord(info);
                  setCorrectionText(info.word);
                  setCorrectionDialogOpen(true);
                }}
              />
            </Box>
          </Paper>
        ))}
      </>
    );
  };

  return (
    <Box sx={{ p: 2, pb: { xs: 10, md: 2 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5">
          <MdSpellcheck /> Resume Spell Check
        </Typography>

        <Button
          variant="contained"
          onClick={scanForErrors}
          disabled={scanning}
          startIcon={scanning ? <CircularProgress size={20} /> : <Refresh />}
        >
          {scanning ? "Scanning..." : "Check Spelling"}
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {renderStatus()}

      <Menu
        open={!!contextMenu}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {suggestions.map((sug) => (
          <MenuItem key={sug} onClick={() => handleReplace(sug)}>
            {sug}
          </MenuItem>
        ))}
      </Menu>

      <Dialog open={correctionDialogOpen} onClose={() => setCorrectionDialogOpen(false)}>
        <DialogTitle>Manual Correction</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            value={correctionText}
            onChange={(e) => setCorrectionText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCorrectionDialogOpen(false)}>Cancel</Button>
          <Button onClick={applyManualCorrection} variant="contained">
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SpellCheckComponent;